import { useMemo } from 'react';
import { Box, Grid, Paper, Stack, Typography } from '@mui/material';
import { StringUtil } from '../../../utils';
import {
  CITYSPRINT_DETAILS_LABELS,
  CITYSPRINT_DETAILS_SECTIONS,
  CITYSPRINT_INNER_PANEL_DARK_BG,
  CITYSPRINT_REVIEW,
} from '../constants';
import {
  buildCitysprintAddressForWidget,
  formatReviewCollectionDateTime,
  formatReviewDateTimeFromIso,
} from '../helpers';

const { joinComma, trimPart } = StringUtil;

const S = CITYSPRINT_DETAILS_SECTIONS;

const muted = { color: 'text.secondary', fontWeight: 400 };
const wrapText = {
  ...muted,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

function ReviewAddressBlock({ address }) {
  const org = trimPart(address.organisation);
  const line1Core = joinComma([address.street, address.locality]);
  const line2Core = joinComma([address.town, address.county, address.postcode]);
  const line1 = line1Core && line2Core ? `${line1Core},` : line1Core || '';

  if (!org && !line1 && !line2Core) {
    return null;
  }

  return (
    <Box sx={{ mb: 1.5, minWidth: 0 }}>
      {org ? (
        <Typography variant='body2' sx={wrapText}>
          {org}
        </Typography>
      ) : null}
      {line1 ? (
        <Typography variant='body2' sx={wrapText}>
          {line1}
        </Typography>
      ) : null}
      {line2Core ? (
        <Typography variant='body2' sx={wrapText}>
          {line2Core}
        </Typography>
      ) : null}
    </Box>
  );
}

function ReviewContactExtras({
  email,
  phone,
  instructions,
  instructionsLabel,
}) {
  const emailLine = trimPart(email);
  const phoneLine = trimPart(phone);
  const instructionsLine = trimPart(instructions);
  const hasContact = Boolean(emailLine || phoneLine);

  if (!hasContact && !instructionsLine) {
    return null;
  }

  return (
    <>
      {hasContact ? (
        <Box sx={{ mb: 1.5, minWidth: 0 }}>
          {emailLine ? (
            <Typography variant='body2' sx={wrapText}>
              {emailLine}
            </Typography>
          ) : null}
          {phoneLine ? (
            <Typography variant='body2' sx={wrapText}>
              {phoneLine}
            </Typography>
          ) : null}
        </Box>
      ) : null}
      {instructionsLine ? (
        <Box sx={{ mb: 1.5, minWidth: 0 }}>
          <Typography variant='body2' sx={{ ...wrapText, fontWeight: 500 }}>
            {instructionsLabel}
          </Typography>
          <Typography
            variant='body2'
            sx={{ ...wrapText, whiteSpace: 'pre-wrap' }}
          >
            {instructionsLine}
          </Typography>
        </Box>
      ) : null}
    </>
  );
}

function LocationColumn({
  title,
  contactName,
  address,
  email,
  phone,
  instructions,
  instructionsLabel,
  byLabel,
  byValue,
}) {
  return (
    <Box
      component='section'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: '100%',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flex: '0 0 auto', minWidth: 0 }}>
        <Typography
          variant='subtitle1'
          component='h3'
          sx={{
            fontWeight: 400,
            mb: 1.5,
            color: 'text.primary',
          }}
        >
          {title}
        </Typography>
        <Typography
          variant='body1'
          sx={{
            fontWeight: 400,
            mb: 0.5,
            color: 'text.primary',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          }}
        >
          {trimPart(contactName) || ''}
        </Typography>
        <ReviewAddressBlock address={address} />
        <ReviewContactExtras
          email={email}
          phone={phone}
          instructions={instructions}
          instructionsLabel={instructionsLabel}
        />
      </Box>
      <Box sx={{ flex: '1 1 auto', minHeight: { xs: 0, md: 8 } }} aria-hidden />
      <Box sx={{ flex: '0 0 auto', pt: 2 }}>
        <Typography
          variant='body2'
          sx={{ fontWeight: 400, mb: 0.5, color: 'text.secondary' }}
        >
          {byLabel}
        </Typography>
        <Typography
          variant='body1'
          sx={{ color: 'primary.main', fontWeight: 400, lineHeight: 1.35 }}
        >
          {byValue}
        </Typography>
      </Box>
    </Box>
  );
}

export function CitysprintReviewShippingCard({ data, collectionSchedule }) {
  const deliveryAddress = buildCitysprintAddressForWidget(
    'delivery',
    data,
    data
  );
  const collectionAddress = buildCitysprintAddressForWidget(
    'collection',
    data,
    data
  );

  const deliveryByDisplay = useMemo(() => {
    const t =
      typeof data?.latestDeliveryTime === 'string'
        ? data.latestDeliveryTime.trim()
        : '';
    return formatReviewDateTimeFromIso(t) ?? '—';
  }, [data?.latestDeliveryTime]);

  const collectionByDisplay = useMemo(
    () => formatReviewCollectionDateTime(data, collectionSchedule),
    [
      data?.bookingMode,
      data?.collectionDate,
      data?.timeHour,
      data?.timeMinute,
      data?.timeReadyHour,
      data?.timeReadyMinute,
      data?.latestCollectionTime,
      collectionSchedule,
    ]
  );

  const nameLine = trimPart(data?.yourFullName);
  const emailLine = trimPart(data?.email);
  const phoneLine = trimPart(data?.mobile);

  return (
    <Paper
      sx={theme => ({
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        boxShadow: '0px 1px 3px rgba(0,0,0,0.08)',
        [theme.getColorSchemeSelector('dark')]: {
          boxShadow: 'none',
        },
      })}
      data-testid='citysprint-review-shipping-card'
    >
      <Typography
        variant='h5'
        component='h2'
        sx={{
          fontWeight: 400,
          fontSize: { xs: '1.125rem', sm: '1.25rem' },
          lineHeight: 1.3,
          color: 'text.primary',
        }}
      >
        {CITYSPRINT_REVIEW.TITLE}
      </Typography>

      <Stack spacing={0.5} sx={{ mb: 3, fontSize: 13, color: 'text.primary' }}>
        {nameLine ? (
          <Typography variant='body2' sx={{ fontWeight: 400 }}>
            {nameLine}
          </Typography>
        ) : null}
        {emailLine ? (
          <Typography variant='body2' sx={{ fontWeight: 400 }}>
            {emailLine}
          </Typography>
        ) : null}
        {phoneLine ? (
          <Typography variant='body2' sx={{ fontWeight: 400 }}>
            {phoneLine}
          </Typography>
        ) : null}
      </Stack>

      <Box
        sx={theme => ({
          borderRadius: 2,
          p: { xs: 2, sm: 2.5 },
          bgcolor: theme.palette.grey[50],
          [theme.getColorSchemeSelector('dark')]: {
            bgcolor: CITYSPRINT_INNER_PANEL_DARK_BG,
          },
        })}
      >
        <Grid
          container
          spacing={{ xs: 3, md: 4 }}
          sx={{ alignItems: 'stretch' }}
        >
          <Grid
            size={{ xs: 12, md: 6 }}
            sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}
          >
            <LocationColumn
              title={S.YOUR_DETAILS}
              contactName={data.collectionFullName}
              address={collectionAddress}
              email={data.collectionEmail}
              phone={data.collectionPhone}
              instructions={data.collectionInstructions}
              instructionsLabel={
                CITYSPRINT_DETAILS_LABELS.COLLECTION_INSTRUCTIONS
              }
              byLabel={CITYSPRINT_REVIEW.COLLECTION_BY_DATETIME}
              byValue={collectionByDisplay}
            />
          </Grid>
          <Grid
            size={{ xs: 12, md: 6 }}
            sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}
          >
            <LocationColumn
              title={S.RECEIVER_DETAILS}
              contactName={data.deliveryFullName}
              address={deliveryAddress}
              email={data.deliveryEmail}
              phone={data.deliveryPhone}
              instructions={data.deliveryInstructions}
              instructionsLabel={
                CITYSPRINT_DETAILS_LABELS.DELIVERY_INSTRUCTIONS
              }
              byLabel={CITYSPRINT_REVIEW.DELIVERY_BY_DATETIME}
              byValue={deliveryByDisplay}
            />
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}
